import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { AiOutlineDelete } from "react-icons/ai";
import { BsImage } from "react-icons/bs";
import { TbArrowsSort } from "react-icons/tb";
import { ActionIcon, Drawer, Group, ScrollArea, Stack, Text, TextInput } from "@mantine/core";
import styles from "./styles/PlaylistModal.module.css";
import { EpisodeLink } from "../episode/EpisodeLink";
import { PlaylistItem } from "@/stores/PlaylistStore";
import { useStores } from "@/hooks/useStores";
import { createDurationMin, getPodcastImage, ImageSizes } from "@/core/helpers";

export const PlaylistModal = observer(() => {
    const { playlistStore } = useStores();
    const { episodes } = playlistStore;
    const [filterText, setFilterText] = useState<string>("");
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const media = window.matchMedia("(max-width: 48em)");
        const update = () => {
            setIsMobile(media.matches);
        };

        update();
        media.addEventListener("change", update);
        return () => {
            media.removeEventListener("change", update);
        };
    }, []);

    const doSort = () => {
        playlistStore.toggleSort();
    };

    useEffect(() => {
        playlistStore.filterEpisodes(filterText);
    }, [filterText, playlistStore]);

    const coll = filterText.length > 0 ? playlistStore.filtered : episodes;

    return (
        <Drawer
            opened={playlistStore.isOpen}
            onClose={() => {
                playlistStore.close();
            }}
            padding={0}
            position="right"
            size={isMobile ? "100%" : 360}
            title={<Text fw={700}>Spellista</Text>}
            zIndex={10003}
            styles={{
                content: {
                    top: 0,
                    height: "100dvh",
                },
                body: {
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    padding: 0,
                },
                header: {
                    borderBottom: "1px solid var(--app-border)",
                },
                inner: {
                    padding: 0,
                },
            }}>
            {episodes.length > 0 && (
                <Group className={styles.filter} justify="space-between" px="md" py="sm">
                    <TextInput
                        className={styles.search}
                        value={filterText}
                        onChange={(event) => {
                            setFilterText(event.currentTarget.value);
                        }}
                        size="xs"
                        type="search"
                        placeholder="Sök..."
                    />
                    <ActionIcon aria-label="Sortera spellista" onClick={doSort} variant="subtle">
                        <TbArrowsSort />
                    </ActionIcon>
                </Group>
            )}

            <ScrollArea className={styles.scroller} offsetScrollbars>
                {episodes.length === 0 && (
                    <Stack align="center" className={styles.empty} gap="sm" px="lg" py="xl">
                        <div className={styles.img}>
                            <BsImage size={48} opacity={0.5} />
                        </div>
                        <Text c="dimmed" ta="center">
                            Du lägger till episoder här genom att klicka "Spela senare" på avsnitt du vill lägga till.
                        </Text>
                    </Stack>
                )}
                {coll.map((item: PlaylistItem) => {
                    return (
                        <Group className={styles.item} gap="md" key={"playlist-" + item.episode.slug} wrap="nowrap">
                            <div className={styles.img}>
                                <img
                                    alt={item.episode.title}
                                    src={getPodcastImage(item.podcast, ImageSizes.Thumb)}
                                    width={64}
                                    height={64}
                                    loading={"lazy"}
                                />
                            </div>
                            <div className={styles.about}>
                                <EpisodeLink shorten={true} episode={item.episode} podcast={item.podcast} />
                                <Text className={styles.podcast} size="sm">
                                    {item.podcast.name}
                                </Text>
                                <Text className={styles.meta} size="xs">
                                    {item.episode.published.slice(0, 10)} - {createDurationMin(item.episode.length)}{" "}
                                    minuter
                                </Text>
                            </div>
                            <div className={styles.actions}>
                                <ActionIcon
                                    aria-label="Ta bort från spellistan"
                                    color="red"
                                    onClick={async () => {
                                        if (window.confirm("Vill du ta bort detta avsnitt från spellistan?")) {
                                            await playlistStore.remove(item.episode);
                                        }
                                    }}
                                    variant="subtle">
                                    <AiOutlineDelete size={18} />
                                </ActionIcon>
                            </div>
                        </Group>
                    );
                })}
            </ScrollArea>
        </Drawer>
    );
});
